import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { formatMoney, formatDate, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { StatCard } from "@/components/common/StatCard";
import { StatusChip } from "@/components/common/StatusChip";
import { CarteraPrepararDialog } from "@/features/finance/CarteraPrepararDialog";

const TRAMOS = ["al_dia", "d1_30", "d31_60", "d61_90", "d90_mas"];

export const CarteraTab = () => {
  const { t } = useT();
  const qc = useQueryClient();
  const [selected, setSelected] = useState([]);
  const [detailCustomer, setDetailCustomer] = useState(null);
  const [prepareOpen, setPrepareOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["cartera"],
    queryFn: async () => (await api.get("/finance/cartera")).data,
  });

  const markUploaded = useMutation({
    mutationFn: (invoiceId) => api.post(`/finance/invoices/${invoiceId}/plataforma`, {}),
    onSuccess: () => {
      toast.success(t("finance.cartera.uploaded_ok"));
      qc.invalidateQueries({ queryKey: ["cartera"] });
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const items = data?.items || [];
  const allSelected = items.length > 0 && selected.length === items.length;
  const detail = items.find((c) => c.customer_id === detailCustomer);

  const toggleAll = (v) => setSelected(v ? items.map((c) => c.customer_id) : []);
  const toggleOne = (id, v) => setSelected((s) => (v ? [...s, id] : s.filter((x) => x !== id)));

  return (
    <div data-testid="finance-cartera-tab" className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard testId="cartera-total-due" label={t("finance.cartera.total_due")} value={formatMoney(data?.total_cents)} />
        <StatCard testId="cartera-customers" label={t("finance.cartera.table.customer")} value={items.length} />
        <StatCard testId="cartera-selected" label={t("finance.cartera.selected", { n: selected.length })} value={selected.length} />
      </div>

      <div className="rounded-[10px] border border-line bg-white">
        <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
          <p className="text-[13px] font-semibold text-ink">{t("finance.cartera.title")}</p>
          {selected.length > 0 && (
            <Button size="sm" data-testid="cartera-prepare-button" onClick={() => setPrepareOpen(true)} className="h-8 bg-ink text-paper hover:opacity-90">
              {t("finance.cartera.prepare")}
            </Button>
          )}
        </div>
        {isLoading ? (
          <div className="space-y-2 p-4">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-[13px] w-full bg-surface-sunken" />)}</div>
        ) : !items.length ? (
          <p className="p-6 text-center text-[13px] text-ink-tertiary" data-testid="cartera-empty">{t("finance.cartera.empty")}</p>
        ) : (
          <table className="w-full text-[12.5px]" data-testid="cartera-table">
            <thead className="bg-surface-sunken text-[11px] uppercase tracking-[0.04em] text-ink-tertiary">
              <tr>
                <th className="w-8 px-3 py-2">
                  <Checkbox data-testid="cartera-select-all" checked={allSelected} onCheckedChange={toggleAll} />
                </th>
                <th className="px-3 py-2 text-left">{t("finance.cartera.table.customer")}</th>
                <th className="px-3 py-2 text-right">{t("finance.cartera.table.total")}</th>
                {TRAMOS.map((tr) => (
                  <th key={tr} className="px-3 py-2 text-right">{t(`finance.cartera.aging.${tr}`)}</th>
                ))}
                <th className="px-3 py-2 text-right">{t("finance.cartera.table.oldest")}</th>
                <th className="px-3 py-2 text-left">{t("finance.cartera.table.last_sent")}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.customer_id} className="cursor-pointer border-b border-line hover:bg-surface-sunken"
                  data-testid={`cartera-row-${c.customer_id}`} onClick={() => setDetailCustomer(c.customer_id)}>
                  <td className="px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.includes(c.customer_id)}
                      onCheckedChange={(v) => toggleOne(c.customer_id, v)} />
                  </td>
                  <td className="px-3 py-1.5">
                    <span className="font-medium text-ink">{c.customer_nombre || "—"}</span>
                    {!!c.plataforma_pendientes && (
                      <span data-testid={`cartera-platform-pending-${c.customer_id}`}
                        className="ml-2 inline-flex items-center gap-1 text-[11px] text-state-warn">
                        <AlertTriangle className="h-3 w-3" /> {t("finance.cartera.platform_pending", { n: c.plataforma_pendientes })}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatMoney(c.total_cents)}</td>
                  {TRAMOS.map((tr) => (
                    <td key={tr} className="px-3 py-1.5 text-right tabular-nums text-ink-secondary">
                      {c.tramos[tr] ? formatMoney(c.tramos[tr]) : "—"}
                    </td>
                  ))}
                  <td className="px-3 py-1.5 text-right">{c.factura_mas_vieja} d</td>
                  <td className="px-3 py-1.5">
                    {c.facturas?.some((f) => f.ultimo_cobro_enviado_at) ? formatDate(
                      c.facturas.map((f) => f.ultimo_cobro_enviado_at).filter(Boolean).sort().slice(-1)[0]
                    ) : "—"}
                  </td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Sheet open={Boolean(detail)} onOpenChange={(v) => !v && setDetailCustomer(null)}>
        <SheetContent data-testid="cartera-detail-sheet" className="w-full overflow-y-auto sm:max-w-lg">
          {detail && (
            <>
              <SheetHeader><SheetTitle>{detail.customer_nombre}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-2">
                {detail.facturas.map((f) => (
                  <div key={f.invoice_id} data-testid={`cartera-invoice-${f.invoice_id}`}
                    className="flex items-center justify-between border-b border-line py-2 text-[12.5px]">
                    <div className="min-w-0">
                      <p className="font-medium text-ink">{f.numero || f.code}</p>
                      <p className="text-ink-tertiary">{formatDate(f.fecha_emision)} · {f.dias} d</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-mono tabular-nums">{formatMoney(f.monto_cents)}</span>
                      {f.plataforma_estado === "pendiente_de_subir" && (
                        <Button size="sm" variant="outline" data-testid={`cartera-mark-uploaded-${f.invoice_id}`}
                          disabled={markUploaded.isPending} onClick={() => markUploaded.mutate(f.invoice_id)} className="h-7 text-[11px]">
                          {t("finance.cartera.mark_uploaded")}
                        </Button>
                      )}
                      {f.plataforma_estado === "subida" && <StatusChip tone="green" label={t("finance.cartera.mark_uploaded")} />}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <CarteraPrepararDialog open={prepareOpen} onOpenChange={setPrepareOpen} customerIds={selected}
        onDone={() => { setSelected([]); qc.invalidateQueries({ queryKey: ["cartera"] }); }} />
    </div>
  );
};
